import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { AnyObject, Form } from 'react-final-form';
import { useTheme } from '../../../theme';
import {
	DocumentType,
	DropdownOption,
	FormStages,
	TravellerDocumentFields,
	TravellerFields,
	TravellerFormType,
	TravellerType
} from '../../types';
import { useTranslation } from 'react-i18next';
import Preferences from '../Preferences/Preferences';
import { TravellerTypes } from '../../../Traveller/types';
import { clearPhoneNumber } from '../../../PhoneInput/utils';
import { successLoadingIcon as successIcon } from '../../Icons';
import { Tooltip } from '../../../BaseComponents';
import { formatInvalidPassengerDate, validate } from '../../../Passenger/utils';
import {
	citizenshipMutator,
	convertDocuments,
	directionsMutator,
	docTypeMutator,
	documentMutator,
	documentsMutator,
	fixEmptyFieldDocumentMutator,
	fixEmptyFieldMutator,
	getInputType
} from '../../utils';
import {
	LocationSuggestionFragment,
	TravellerFieldEnum,
	TravellerParameterFragment,
	UserProfileParameters
} from '@websky/graphql';
import DocumentsSection from '../DocumentsSection/DocumentsSection';
import SectionWrapper from '../SectionWrapper/SectionWrapper';
import PersonalInfoSection from '../PersonalInfoSection/PersonalInfoSection';
import ContactsSection from '../ContactsSection/ContactsSection';
import ButtonBase from '../../../BaseComponents/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { FormApi } from 'final-form';
import { convertGenderToCorrectValue, MOBILE_MIN_WIDTH, useIsMobile } from '../../../utils';
import DirectionsSection from '../DirectionsSection/components/DirectionsSection';
import { CdnIcon } from '@shared/ui';
import MediaQuery from 'react-responsive';
import { DocumentFields } from '../../consts';

const SET_STAGE_TIMEOUT = 500;
const ERROR_TIMEOUT = 5000;

const hasDocumentErrors = (errors: unknown): boolean =>
	Array.isArray(errors) && errors.some(docErrors => docErrors && Object.keys(docErrors as object).length > 0);

interface Props {
	traveller: TravellerType;
	cities?: LocationSuggestionFragment[];
	type?: TravellerTypes;
	showPreferences?: boolean;
	primaryCountryCodes?: string[];
	onSubmit: (values: UserProfileParameters) => Promise<boolean>;
	onDelete?: () => Promise<boolean>;
	onClose?: () => void;
}

const TravellerForm: React.FC<Props> = ({
	traveller,
	cities,
	type,
	showPreferences,
	primaryCountryCodes,
	onSubmit,
	onDelete,
	onClose
}) => {
	const { TravellerForm: css } = useTheme('TravellerForm');

	const { t: tTraveller } = useTranslation('Traveller');
	const { t: tPassenger } = useTranslation('Passenger');

	const [stage, setStage] = useState(FormStages.Default);
	const [openedErrorTooltip, setOpenedErrorTooltip] = useState(false);
	const documentsSectionRef = useRef<((index: number) => void) | undefined>();
	const isMobile = useIsMobile();

	const isLoading = stage === FormStages.Loading;

	/**
	 * Выбор направлений отображаем только для профиля пользователя.
	 * Если в админке показ направлений выключен, то preferredDepPoints/preferredArrPoints === null
	 * Если включен, то приходит пустой массив или выбранные направления.
	 */
	const showDirectionsFields =
		type === TravellerTypes.PersonalDetails &&
		cities?.length &&
		traveller.preferredDepPoints &&
		traveller.preferredArrPoints;

	const citiesMap = useMemo(() => new Map(cities?.map(c => [c.iata, c]) || []), [cities]);

	const fieldValuesObject = useMemo<TravellerFormType>(() => {
		const values: TravellerFormType = {};

		for (const field of traveller.values) {
			if (field.type === TravellerFieldEnum.Gender) {
				(values as any)[field.name] = convertGenderToCorrectValue(field.value);
			} else {
				(values as any)[field.name] = formatInvalidPassengerDate(field.type, field.value);
			}
		}

		if (traveller.documents?.length) {
			values[TravellerFields.Nationality] = traveller.documents[0].citizenship;
			values[TravellerFields.Documents] = [...convertDocuments(traveller.documents)] as DocumentType[];
		} else {
			values[TravellerFields.Documents] = [];
		}

		if (traveller.preferredDepPoints) {
			const departure: DropdownOption[] = [];

			traveller.preferredDepPoints.forEach(point => {
				const city = citiesMap.get(point);

				if (city) {
					departure.push({ label: city.name, value: city.iata });
				}
			});

			values[TravellerFields.Departure] = departure;
		}

		if (traveller.preferredArrPoints) {
			const arrival: DropdownOption[] = [];

			traveller.preferredArrPoints.forEach(point => {
				const city = citiesMap.get(point);

				if (city) {
					arrival.push({ label: city.name, value: city.iata });
				}
			});

			values[TravellerFields.Arrival] = arrival;
		}

		values[TravellerFields.DepartureQuery] = '';
		values[TravellerFields.ArrivalQuery] = '';

		return values;
	}, [JSON.stringify(traveller), citiesMap]);

	const defaultFormValues = useMemo<Record<string, TravellerParameterFragment>>(() => {
		const res: Record<string, TravellerParameterFragment> = {};

		traveller.values.forEach(v => (res[v.name] = v));

		traveller.documents[0]?.values.forEach(v => (res[v.name] = v));

		return res;
	}, [traveller]);

	const handleDocumentErrorsOnSubmit = (form: FormApi<TravellerFormType>) => {
		const { submitFailed, errors, values: formValues } = form.getState();
		const documentsList = formValues.documents ?? [];

		if (!submitFailed || documentsList.length === 0) {
			return;
		}

		documentsList.forEach((_, index) => {
			DocumentFields.forEach(field => form.blur(`documents[${index}].${field}`));
		});

		if (isMobile && hasDocumentErrors(errors?.documents)) {
			documentsSectionRef.current?.(0);
		}
	};

	const onPreInnerSubmit = (
		event: React.FormEvent<HTMLFormElement>,
		handleSubmit: (event?: React.SyntheticEvent<HTMLFormElement, Event>) => Promise<AnyObject>,
		form: FormApi<TravellerFormType>
	) => {
		if (!form.getState().values.documents?.length) {
			form.mutators.documentsMutator({ type: 'add' });
		}

		const submitResult = handleSubmit(event);

		if (!submitResult) {
			handleDocumentErrorsOnSubmit(form);

			return;
		}

		submitResult.then(res => {
			if (form.getState().submitFailed) {
				handleDocumentErrorsOnSubmit(form);

				return;
			}

			if (res) {
				setStage(FormStages.Success);
				if (type !== TravellerTypes.PersonalDetails) {
					setTimeout(() => {
						form.reset();
						setStage(FormStages.Default);
					}, SET_STAGE_TIMEOUT);
				} else {
					setTimeout(() => {
						setStage(FormStages.Default);
					}, SET_STAGE_TIMEOUT);
				}
			} else {
				setStage(FormStages.Default);
				setOpenedErrorTooltip(true);
				setTimeout(() => {
					setOpenedErrorTooltip(false);
				}, ERROR_TIMEOUT);
			}
		});
	};

	const onInnerSubmit = async (values: TravellerFormType) => {
		setStage(FormStages.Loading);

		const data = await onSubmit({
			firstName: values.firstName,
			lastName: values.lastName,
			middleName: values.middleName,
			citizenship: values.nationality,
			birthDate: values.birthDate,
			gender: values.gender,
			email: values.email,
			phone: values.phone && clearPhoneNumber(values.phone),
			documents: values.documents.map(doc => ({
				type: doc.docType,
				number: doc.docNumber,
				expiryDate: doc.docExpiryDate,
				citizenship: doc.citizenship,
				firstName: doc.firstName,
				lastName: doc.lastName,
				middleName: doc.middleName
			})),
			preferredDepPoints: values.departure?.map(dep => dep.value),
			preferredArrPoints: values.arrival?.map(arr => arr.value)
		});

		if (data && onClose) {
			setTimeout(onClose, 500);
		}

		return data;
	};

	const validateForm = (allValues: TravellerFormType): TravellerFormType => {
		const documents: DocumentType[] = [];

		function validateValues(values: Record<string, any>) {
			const valuesErrors: Record<string, string> = {};

			for (const docFieldItem of Object.entries(values)) {
				const fieldName = docFieldItem[0];
				const inputType = getInputType(fieldName);
				const inputValue = docFieldItem[1] as string;

				const validationRules = defaultFormValues[fieldName]?.validationRules;

				if (validationRules) {
					const error = validate(inputValue, values, inputType, validationRules);

					if (error) {
						valuesErrors[fieldName] = tPassenger(error);
					}
				}
			}

			return valuesErrors;
		}

		const commonFieldsErrors = validateValues(allValues);

		allValues.documents?.forEach(doc => {
			const docErrors = validateValues(doc);

			[
				TravellerDocumentFields.FirstName,
				TravellerDocumentFields.LastName,
				TravellerDocumentFields.DocType,
				TravellerDocumentFields.DocNumber,
				TravellerDocumentFields.Citizenship
			].forEach(field => {
				if (!doc[field as keyof DocumentType] && !docErrors[field]) {
					docErrors[field] = tPassenger('Required');
				}
			});

			documents.push(docErrors);
		});

		return {
			...commonFieldsErrors,
			documents
		};
	};

	const renderLoading = (button: JSX.Element) => {
		return isLoading ? (
			<div className={css.loader}>
				<CircularProgress size="100%" classes={{ root: css.circularProgress }} />
			</div>
		) : stage === FormStages.Success ? (
			<div className={cn(css.loader, css.success)}>
				<div className={css.success__icon}>{successIcon} </div>
			</div>
		) : (
			button
		);
	};

	const onCancel = React.useCallback(
		(form: FormApi<TravellerFormType>) => {
			onClose();
			form.reset();
		},
		[onClose]
	);

	const removeButton =
		type === TravellerTypes.CompanionFilled ? (
			<ButtonBase className={css.removeButton} onClick={onDelete} disabled={isLoading} variant="text">
				<CdnIcon icon={'Action/Remove'} />

				{tTraveller('Remove passengers data')}
			</ButtonBase>
		) : null;

	return (
		<div className={css.travellerForm}>
			<Form
				initialValues={fieldValuesObject}
				onSubmit={onInnerSubmit}
				validate={validateForm}
				mutators={{
					documentsMutator,
					documentMutator,
					docTypeMutator,
					citizenshipMutator,
					fixEmptyFieldMutator,
					fixEmptyFieldDocumentMutator,
					directionsMutator
				}}
				render={({ handleSubmit, form, values }) => {
					return (
						<form
							onSubmit={event => {
								onPreInnerSubmit(event, handleSubmit, form);
							}}
						>
							<div className={css.travellerForm__content}>
								<DocumentsSection
									documentsSectionRef={documentsSectionRef}
									documents={values.documents}
									traveller={traveller}
									primaryCountryCodes={primaryCountryCodes}
								/>
								<SectionWrapper title={tTraveller('Personal info')}>
									<PersonalInfoSection />
								</SectionWrapper>

								{showDirectionsFields && (
									<SectionWrapper title={tTraveller('Directions')}>
										<div className={css.directions}>
											<p className={css.description}>
												{tTraveller(
													'With this data we will be able to help select destinations when booking and send the best deals on these routes'
												)}
											</p>

											<DirectionsSection cities={cities} />
										</div>
									</SectionWrapper>
								)}

								<SectionWrapper title={tTraveller('Contacts')}>
									<div className={css.directions}>
										<p className={css.description}>
											{tTraveller(
												'With the help of this data, we will be able to inform the fellow traveler about changes in booking, flight status, check-in time, etc. Optional data.'
											)}
										</p>
										<ContactsSection />
									</div>
								</SectionWrapper>

								{showPreferences && (
									<Preferences
										Meal={['Vegetarian menu', 'same another']}
										Seats={['Window seat', 'Center seat']}
									/>
								)}

								<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>{removeButton}</MediaQuery>

								<div className={cn(css.travellerForm__contentControls)}>
									<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>{removeButton}</MediaQuery>

									<div className={css.travellerForm__actions}>
										{renderLoading(
											<>
												{onClose && (
													<ButtonBase
														variant="smallSecondary"
														className={css.cancelButton}
														onClick={() => onCancel(form)}
													>
														{tTraveller('Cancel')}
													</ButtonBase>
												)}

												<Tooltip
													title={tTraveller(
														'An error occurred while executing the request, please try again'
													)}
													open={openedErrorTooltip}
												>
													<ButtonBase
														className={css.saveButton}
														variant="smallPrimary"
														type="submit"
													>
														{tTraveller('Save')}
													</ButtonBase>
												</Tooltip>
											</>
										)}
									</div>
								</div>
							</div>
						</form>
					);
				}}
			/>
		</div>
	);
};

export default TravellerForm;
